// Local-mode mocks for database policies (/pam/policies/database) and the
// lookups its Where section needs (databaseService hosts, databases, roles,
// tables, columns).
//
// The policy service endpoints are shared with AD, RADIUS and Apps (answered
// by idpMocks.js). These entries sit before IDP_MOCKS and answer only for
// database policies; anything else returns MOCK_PASS, which tells
// findMockResponse (mockData.js) to keep looking.

export const MOCK_PASS = Symbol("mock-pass");

// A mock answer with this key set is sent with that HTTP status instead of
// 200 (index.js), e.g. the policy service's 409 for a duplicate.
export const MOCK_HTTP_STATUS = "__mockHttpStatus";

// The policy type a policy-service call is about. ListPolicy ignores a
// top-level policyType and filters on filters[{ filterParameter: "policyType" }],
// as the backend does; create and update carry it at the top level.
export const askedPolicyType = (body) => {
  const filter = (body?.filters || []).find((f) => f?.filterParameter === "policyType");
  return String(filter ? filter.filterValue : body?.policyType || body?.policyJson?.policyType || "").toLowerCase();
};
// ListPolicy: only the filter counts (a top-level policyType is ignored).
export const listedPolicyType = (body) => {
  const filter = (body?.filters || []).find((f) => f?.filterParameter === "policyType");
  return filter ? String(filter.filterValue).toLowerCase() : "";
};

const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);

/**
 * UpdateJSONPolicy's merge (authnull-service policy-save-merge): top-level
 * keys left out are kept; conditions and actions are replaced whole when sent
 * and null clears them (as does null for any key); other objects (database,
 * ssh, ...) are merged one level deep.
 */
export function mergePolicyJson(stored, sent) {
  if (!isPlainObject(sent)) return { ...(stored || {}) };
  const out = { ...(stored || {}) };
  Object.entries(sent).forEach(([k, v]) => {
    if (v === null) delete out[k];
    else if (k === "conditions" || k === "actions") out[k] = v;
    else if (isPlainObject(v) && isPlainObject(out[k])) out[k] = { ...out[k], ...v };
    else out[k] = v;
  });
  return out;
}

const now = () => new Date().toISOString();
const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
const isDb = (body) => askedPolicyType(body) === "database";

// host -> databases; database -> roles, tables, columns.
const DB_HOSTS = {
  "10.20.0.11": [
    { db_name: "orders", db_type: "MySQL" },
    { db_name: "billing", db_type: "PostgreSQL" },
  ],
  "10.20.0.12": [{ db_name: "analytics", db_type: "PostgreSQL" }],
};
const DB_ROLES = { orders: ["app_ro", "app_rw", "root"], billing: ["billing_ro", "billing_rw", "postgres"], analytics: ["analyst", "etl"] };
const DB_TABLES = {
  orders: { customers: ["id", "name", "email", "phone"], invoices: ["id", "customer_id", "amount", "card_last4"], items: ["id", "sku", "price"] },
  billing: { accounts: ["id", "iban"], payments: ["id", "amount"] },
  analytics: { events: ["id", "kind", "at"] },
};

const policies = [
  {
    id: "db000000-0000-4000-8000-000000000001",
    policyName: "Orders support read",
    policyType: "database",
    status: "Approved",
    version: 1,
    createdAt: "2026-10-20T09:00:00Z",
    updatedAt: "2026-10-20T09:00:00Z",
    policyJson: {
      policyName: "Orders support read",
      policyType: "database",
      // Saved before the action existed: blank means Require MFA.
      policyFlow: "",
      database: {
        host: "10.20.0.11",
        database_name: "orders",
        user: "app_ro",
        privilege: ["READ"],
        tables: ["customers", "invoices", "items"],
        field_masking: { customers: ["email", "phone"], invoices: ["card_last4"] },
      },
      permissions: { allowed: true, iam_users: ["sumit@authnull.com", "aditya@authnull.com"], iam_groups: [] },
    },
  },
  {
    id: "db000000-0000-4000-8000-000000000002",
    policyName: "Billing writers",
    policyType: "database",
    status: "Approved",
    version: 1,
    createdAt: "2026-10-21T09:00:00Z",
    updatedAt: "2026-10-21T09:00:00Z",
    policyJson: {
      policyName: "Billing writers",
      policyType: "database",
      policyFlow: "allow",
      database: { host: "10.20.0.11", database_name: "billing", database_type: "postgres", user: "billing_rw", privilege: ["READ", "WRITE"], tables: ["accounts", "payments"], field_masking: null },
      permissions: { allowed: true, iam_users: ["saffran@authnull.com"], iam_groups: [] },
      conditions: { time: { days: ["mon", "tue", "wed", "thu", "fri"], start: "09:00", end: "18:00", timezone: "Asia/Kolkata" } },
    },
  },
  {
    id: "db000000-0000-4000-8000-000000000003",
    policyName: "No root on orders",
    policyType: "database",
    status: "Suspended",
    version: 1,
    createdAt: "2026-10-22T09:00:00Z",
    updatedAt: "2026-10-22T09:00:00Z",
    policyJson: {
      policyName: "No root on orders",
      policyType: "database",
      policyFlow: "block",
      database: { host: "10.20.0.11", database_name: "orders", database_type: "mysql", user: "root", privilege: ["READ", "WRITE", "EXECUTE"], tables: [], field_masking: {} },
      permissions: { allowed: true, iam_users: ["guptasatyam1807@gmail.com"], iam_groups: [] },
    },
  },
];

const indexOf = (id) => policies.findIndex((p) => p.id === id);

// Overlaps as the backend judges them (db-policy-overlap): an exact duplicate
// (same database, role, engine, action and users, and neither has conditions
// or is a fallback) is refused with 409 unless force; any other overlap (same
// database and role, a user in common, compatible engines) is created with a
// warning.
const dbFlowOf = (pj) => {
  const f = String(pj?.policyFlow || "").trim().toLowerCase();
  if (!f || f === "mfa" || f === "mfa_required") return "mfa_required";
  if (f === "deny") return "block";
  return f;
};
const dbUsers = (pj) => [...new Set((pj?.permissions?.iam_users || []).map((u) => String(u).trim().toLowerCase()).filter(Boolean))].sort();
const hasConditions = (pj) => isPlainObject(pj?.conditions) && Object.keys(pj.conditions).length > 0;
const plainPolicy = (pj) => !hasConditions(pj) && !pj?.fallback;
const dbType = (pj) => String(pj?.database?.database_type || "").toLowerCase();
const sameScope = (a, b) => a?.database?.database_name === b?.database?.database_name && a?.database?.user === b?.database?.user;
function dbOverlaps(pj) {
  const users = dbUsers(pj);
  const live = policies.filter((p) => String(p.status || "").toLowerCase() !== "deleted" && sameScope(p.policyJson, pj));
  const exact = live.filter(
    (p) =>
      dbType(p.policyJson) === dbType(pj) &&
      dbFlowOf(p.policyJson) === dbFlowOf(pj) &&
      JSON.stringify(dbUsers(p.policyJson)) === JSON.stringify(users) &&
      plainPolicy(p.policyJson) &&
      plainPolicy(pj),
  );
  const overlap = live.filter(
    (p) =>
      !exact.includes(p) &&
      (!dbType(p.policyJson) || !dbType(pj) || dbType(p.policyJson) === dbType(pj)) &&
      dbUsers(p.policyJson).some((u) => users.includes(u)),
  );
  return { exact, overlap };
}
const ok = (extra = {}) => ({ code: 200, status: "success", message: "OK", ...extra });

const DB_POLICY_MOCKS = [
  {
    pattern: "/policyservice/listpolicy",
    respond: (body = {}) => {
      if (listedPolicyType(body) !== "database") return MOCK_PASS;
      const rows = policies.map((p) => ({ ...p }));
      return ok({ data: rows, policies: rows, total: rows.length, total_pages: 1 });
    },
  },
  {
    pattern: "/policyservice/createjsonpolicy",
    respond: (body = {}) => {
      if (!isDb(body)) return MOCK_PASS;
      const { exact, overlap } = dbOverlaps(body.policyJson || {});
      if (exact.length && !body.force) {
        return {
          [MOCK_HTTP_STATUS]: 409,
          code: 409,
          status: "failed",
          message: "Policy duplicates an existing policy",
          data: "",
          conflicts: exact.map((p) => ({ ...p })),
        };
      }
      const clash = [...exact, ...overlap];
      const id = uuid();
      policies.push({
        id,
        policyName: body.policyName || body.policyJson?.policyName || "",
        policyType: "database",
        status: "Approved",
        version: 1,
        createdAt: now(),
        updatedAt: now(),
        policyJson: { ...body.policyJson, policyType: "database" },
      });
      const warning = clash.length ? `Overlaps ${clash.map((p) => p.policyName).join(", ")}: the most restrictive applies.` : "";
      return ok({ message: "Successfully created policy", data: id, ...(warning ? { warning } : {}) });
    },
  },
  {
    // Editing makes a new version: a new id, and the old one is no longer listed.
    pattern: "/policyservice/updatejsonpolicy",
    respond: (body = {}) => {
      const i = indexOf(body.id);
      if (i < 0) return MOCK_PASS;
      const old = policies[i];
      const next = {
        ...old,
        id: uuid(),
        version: (Number(old.version) || 1) + 1,
        policyName: body.policyName || old.policyName,
        policyJson: { ...mergePolicyJson(old.policyJson, body.policyJson), policyType: "database" },
        updatedAt: now(),
      };
      policies.splice(i, 1, next);
      return { updatedPolicyID: { code: 200, status: "success", message: "Policy updated", data: next.id } };
    },
  },
  {
    pattern: "/policyservice/deletepolicyjson",
    respond: (body = {}) => {
      const i = indexOf(body.policyId);
      if (i < 0) return MOCK_PASS;
      policies.splice(i, 1);
      return ok({ message: "Policy deleted" });
    },
  },
  {
    // Suspend / resume: { action: { actionType: "status", actionIds, actionValue } }.
    pattern: "/policyservice/policyaction",
    respond: (body = {}) => {
      const a = body.action || {};
      const mine = policies.filter((p) => (a.actionIds || []).includes(p.id));
      if (!mine.length || a.actionType !== "status") return MOCK_PASS;
      mine.forEach((p) => {
        p.status = a.actionValue;
      });
      return ok({ message: "Policy updated" });
    },
  },
  {
    pattern: "/policyservice/approvepolicyagentless",
    respond: (body = {}) => {
      const i = indexOf(body.policyId);
      if (i < 0) return MOCK_PASS;
      policies[i].status = "Approved";
      return ok({ message: "Policy approved" });
    },
  },

  // databaseService lookups. Its axios instance carries the
  // /api/v1/databaseService prefix in baseURL, so the mock adapter only sees
  // "/listDatabase" etc.: these match the whole last path segment (a RegExp
  // pattern) so "/listUserCount" or "/listDatabaseEndpoints" aren't caught.
  { pattern: /\/listuniquehosts(\?|$)/, respond: () => ok({ data: Object.keys(DB_HOSTS) }) },
  {
    pattern: /\/listdatabase(\?|$)/,
    respond: (body = {}) => {
      const host = (body.filters || []).find((f) => f?.filterParameter === "Host")?.filterValue;
      const hosts = host ? [host] : Object.keys(DB_HOSTS);
      const data = hosts.flatMap((h, hi) => (DB_HOSTS[h] || []).map((d, i) => ({ id: hi * 10 + i + 1, host: h, status: "Active", ...d })));
      return ok({ data, totalCount: data.length });
    },
  },
  {
    pattern: /\/listuser(\?|$)/,
    respond: (body = {}) => {
      const db = (body.filters || []).find((f) => f?.filterParameter === "Database")?.filterValue;
      return ok({ data: (DB_ROLES[db] || []).map((username, i) => ({ id: i + 1, username, database: db })) });
    },
  },
  { pattern: /\/gettablenames(\?|$)/, respond: (body = {}) => ok({ table_names: Object.keys(DB_TABLES[body.DbName] || {}) }) },
  {
    pattern: /\/gettablefields(\?|$)/,
    respond: (body = {}) => {
      const tables = DB_TABLES[body.tenantName] || {};
      return ok({ fields: Object.fromEntries((body.tableNames || []).filter((t) => tables[t]).map((t) => [t, tables[t]])) });
    },
  },
];

export { DB_POLICY_MOCKS };
